import { useMutation, useQueryClient } from '@tanstack/react-query';
import snakeCase from 'lodash-es/snakeCase';

import { getJwt, removeJwt, setJwt } from '@jane/shared/auth';

import { api } from '../api/api';
import { userQueryKeys } from '../user/user.util';
import { withAuthHeader } from '../utils/withAuthHeader';
import type {
  LoginCredentials,
  RegisterCredentials,
} from './authentication.types';
import { authenticationUrls } from './authentication.util';

const login = async (credentials: LoginCredentials) => {
  const response = await api.post<Response>(
    authenticationUrls.userSession(),
    {
      user: credentials,
    },
    undefined,
    true
  );

  const body = await response.json();
  const authorizationHeader = response.headers.get('Authorization');
  if (authorizationHeader) {
    setJwt(authorizationHeader);
  }

  return body;
};

const logout = async () => {
  await api.delete(authenticationUrls.userSession());
};

const register = async ({
  app_mode_at_registration,
  ...user
}: RegisterCredentials) => {
  const response = await api.post<Response>(
    authenticationUrls.userRegister(),
    {
      user: {
        ...(({ promotionsOptIn: _promotionsOptIn, ...rest }) => rest)(user),
        app_mode_at_registration: snakeCase(app_mode_at_registration),
      },
      promotions_opt_out:
        user.promotionsOptIn !== undefined ? !user.promotionsOptIn : undefined,
    },
    undefined,
    true
  );

  const body = await response.json();
  const authorizationHeader = response.headers.get('Authorization');
  if (authorizationHeader) {
    setJwt(authorizationHeader);
  }

  return body;
};

const updateUser = async ({
  appMode,
  storeId,
}: {
  appMode: string;
  storeId: string | number;
}) => {
  const jwt = getJwt();
  return await api.put<Response>(
    authenticationUrls.userRegister(),
    {
      user: {
        app_mode_at_registration: appMode,
        store_id_at_registration: storeId,
      },
    },
    withAuthHeader({}, jwt)
  );
};

const resetPassword = async ({ email }: { email: string }) => {
  const jwt = getJwt();
  return await api.post<Response>(
    authenticationUrls.resetPassword(),
    { email },
    withAuthHeader({}, jwt)
  );
};

const authenticateGoogleCode = async (auth: unknown) => {
  const response = await api.post<Response>(
    authenticationUrls.googleAuthentication(),
    auth,
    undefined,
    true
  );

  const body = await response.json();
  const authorizationHeader = response.headers.get('Authorization');
  if (authorizationHeader) {
    setJwt(authorizationHeader);
  }

  return body;
};

const authenticateAppleCode = async (auth: unknown) => {
  const response = await api.post<Response>(
    authenticationUrls.appleAuthentication(),
    auth,
    undefined,
    true
  );

  const body = await response.json();
  const authorizationHeader = response.headers.get('Authorization');
  if (authorizationHeader) {
    setJwt(authorizationHeader);
  }

  return body;
};

export const useLogin = ({
  onError,
  onSuccess,
}: {
  onError?: (response: any) => void;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: login,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.janeUser() });
      onSuccess && onSuccess();
    },
    onError: (response) => {
      onError && onError(response);
    },
  });
};

export const useLogout = ({ onSuccess }: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      removeJwt();
      queryClient.invalidateQueries({ queryKey: userQueryKeys.janeUser() });
      onSuccess && onSuccess();
    },
  });
};

export const useRegister = ({
  onError,
  onSuccess,
}: {
  onError?: (response: any) => void;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: register,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.janeUser() });
      onSuccess && onSuccess();
    },
    onError: async (response: any) => {
      const text = await response.response.text();
      const errorData = JSON.parse(text);
      onError && onError(errorData.errors.validations);
    },
  });
};

export const useResetPassword = ({
  onError,
  onSuccess,
}: {
  onError?: (response: any) => void;
  onSuccess?: () => void;
}) =>
  useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      onSuccess && onSuccess();
    },
    onError: (response) => {
      onError && onError(response);
    },
  });

export const useAuthenticateGoogleCode = ({
  appMode,
  onError,
  onSuccess,
  storeId,
}: {
  appMode: string;
  onError?: (response: any) => void;
  onSuccess?: (newUser: boolean) => void;
  storeId: string | number;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authenticateGoogleCode,
    onSuccess: (body) => {
      const { user } = body;
      if (user && user.new_oauth) {
        updateUser({ appMode, storeId });
      }
      queryClient.invalidateQueries({ queryKey: userQueryKeys.janeUser() });
      onSuccess && onSuccess(!!user.new_oauth);
    },
    onError: (response) => {
      onError && onError(response);
    },
  });
};

export const useAuthenticateAppleCode = ({
  appMode,
  onError,
  onSuccess,
  storeId,
}: {
  appMode: string;
  onError?: (response: any) => void;
  onSuccess?: (newUser: boolean) => void;
  storeId: string | number;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authenticateAppleCode,
    onSuccess: (body) => {
      const { user } = body;
      if (user && user.new_oauth) {
        updateUser({ appMode, storeId });
      }
      queryClient.invalidateQueries({ queryKey: userQueryKeys.janeUser() });
      onSuccess && onSuccess(!!user.new_oauth);
    },
    onError: (response) => {
      onError && onError(response);
    },
  });
};
